import React, { createContext, useEffect, useState } from 'react';
import plainText from './plainText';
import textarea from './textarea';
import select from './select';
import checkbox from './checkbox';
import date from './date';
import upload from './upload';
import submit from './submit';
import contact from './contact';
import child from './child';
import hazkara from './hazkara';
import calendar from './calendar';
import formBlock from './formBlock';

import steps from './steps';
import condition from './condition';
import identity from './identity';
import product from './product';
import productInfo from './productInfo';
import productTotal from './productTotal';
import freeAmount from './freeAmount';
import paiementMethod from './paiementMethod';

import contactDdn from './contactDdn';

import { notification } from 'antd';
import { injectLayoutParams } from '../layout';
import moment from 'moment';
import dayjs from 'dayjs';
import empty from './empty';
import currencies from 'currencies.json';
import { t } from "@lingui/core/macro"


var BASE_URL = "https://api.kehila.syn-assist.fr/v1"
if (process.env.NODE_ENV !== "production") {
    BASE_URL = "https://kehilaapi.syn-assist.fr/v1"
}


export const FormFieldPath=createContext({path:[]})


export function removeFieldType(fieldName) {
    return fieldName.replace(/^\[[A-Za-z0-9_]*\]_/, "")
}

export function DefaultValueProvider({ value, onChange, defaultValue, children }) {
    const hasDefault = defaultValue !== undefined;
    const [initialized, setInitialized] = React.useState(false);

    // Si value est undefined et qu’un defaultValue existe → on le pousse une fois
    React.useEffect(() => {
        if (!initialized && value === undefined && hasDefault && onChange) {
            onChange(defaultValue);
            setInitialized(true);
        }
    }, [initialized, value, hasDefault, defaultValue, onChange]);

    const handleChange = (newVal) => {
        const next = newVal?.target?.value ?? newVal;
        onChange?.(next);
    };

    return React.Children.map(children, (child) =>
        React.isValidElement(child)
            ? React.cloneElement(child, {
                value: value ?? (hasDefault ? defaultValue : ""), // fallback d’affichage
                onChange: handleChange,
            })
            : child
    );
}

export function FixedValueProvider({ value, defaultValue, onChange, children }) {
    const hasDefault = defaultValue !== undefined;

    // 🔁 Si un defaultValue est défini : on force la valeur du parent à rester égale
    React.useEffect(() => {
        if (hasDefault && value !== defaultValue && onChange) {
            onChange(defaultValue);
        }
    }, [hasDefault, value, defaultValue, onChange]);

    const handleChange = (newVal) => {
        const next = newVal?.target?.value ?? newVal;

        if (hasDefault) {
            // Ignore toute tentative de modification
            if (onChange && next !== defaultValue) {
                onChange(defaultValue);
            }
        } else {
            // Pass-through standard
            onChange?.(next);
        }
    };

    const currentValue = hasDefault ? defaultValue : value;

    return React.Children.map(children, (child) =>
        React.isValidElement(child)
            ? React.cloneElement(child, {
                value: currentValue,
                onChange: handleChange,
            })
            : child
    );
}



export function MomentToDate({value, onChange, children}) {

    const convertToMoment = (v) => {
        if (!v) {
            return null
        }

        if ((Array.isArray(v) && v[0] instanceof dayjs) || (!Array.isArray(v) && v instanceof dayjs)) {
            //DAYJS
            if (v || (Array.isArray(v) && v.length === 2)) {
                return Array.isArray(v) ? [dayjs(v[0]), dayjs(v[1])] : dayjs(v)
            }
        } else {
            if (v || (Array.isArray(v) && v.length === 2)) {
                return Array.isArray(v) ? [moment(v[0]), moment(v[1])] : moment(v)
            }
        }

        return null
    }
    const convertToDate = (v) => {
        if (v || (Array.isArray(v) && v.length === 2)) {
            return Array.isArray(v) ? [v[0].toDate(), v[1].toDate()] : v.toDate()
        }
        return null
    }

    return React.Children.map(children, child => {
        // Checking isValidElement is the safe way and avoids a
        // typescript error too.
        if (React.isValidElement(child)) {
            return React.cloneElement(child, {
                value: convertToMoment(value),
                onChange: (v) => onChange(convertToDate(v))
            });
        }
        return child;
    });
}


class EventEmitter {
    constructor() {
        this._events = new Map();
    }

    on(event, callback) {
        if (!this._events.has(event)) {
            this._events.set(event, new Set());
        }
        this._events.get(event).add(callback);
    }

    off(event, callback) {
        const callbacks = this._events.get(event);
        if (callbacks) {
            callbacks.delete(callback);
            if (callbacks.size === 0) this._events.delete(event);
        }
    }

    emit(event, data) {
        const callbacks = this._events.get(event);
        if (callbacks) {
            for (const cb of callbacks) {
                try {
                    cb(data);
                } catch (e) {
                    console.error(`[EventEmitter] Error in '${event}':`, e);
                }
            }
        }
    }

    removeAll(event) {
        this._events.delete(event);
    }
}

export const CONDITIONS = [
    `Est égal à`,
    `Est supérieur ou égal à`,
    `Est inférieur ou égal à`,
    `N'est pas égal à`,
    `Est vide`,
    `N'est pas vide`,
    `Est coché`,
]

export default class FormPlugin extends EventTarget {

    VALIDATION = [
        `Alphabétique`,
        `Alphanumérique`,
        `Numérique`,
        `Téléphone`,
        `Email`,
        `Url`
    ]

    DATE_FILTER_SELECT = [
        `Antérieur à aujourd'hui`,
        `Ultérieur à aujourd'hui`,
        `Aujourd'hui & antérieur à aujourd'hui`,
        `Aujourd'hui & ultérieur à aujourd'hui`,
    ]

    DATE_PICKER = [
        `Date & Heure`,
        `Uniquement les Années`,
        `Uniquement les Mois`,
        `Uniquement l'heure`,
    ]

    UPLOAD_TYPE = [
        `Image`,
        `Fichier Pdf`,
        `Fichier Zip`,
    ]


    RELATIONS_OPTIONS = [
        {
            labelH: t`Le Conjoint`,
            labelF: t`La Conjointe`,
            value: "MARI"
        },
        {
            labelH: t`Le Père`,
            labelF: t`La Mère`,
            value: "PERE"
        },
        {
            labelH: t`Le Fils`,
            labelF: t`La Fille`,
            value: "ENFANT"
        },
        {
            labelH: t`Le Frere`,
            labelF: t`La Soeur`,
            value: "FRERE"
        },
        {
            labelH: t`Le Grand Père`,
            labelF: t`La Grand Mère`,
            value: "GP"
        },
        {
            labelH: t`Le Petit Fils`,
            labelF: t`La Petite Fille`,
            value: "PF"
        },
        {
            labelH: t`l'Ex Conjoint`,
            labelF: t`l'Ex Conjointe`,
            value: "EX"
        },
        {
            labelH: t`Le Beau Frere`,
            labelF: t`La Belle Sœur`,
            value: "BF"
        },
        {
            labelH: t`Le Cousin`,
            labelF: t`La Cousine`,
            value: "COUSIN"
        },
        {
            labelH: t`l'ami`,
            labelF: t`l'amie`,
            value: "AMI"
        },
    ]

    namespaces = [{key: "/", formPrefix: null}]

    org = null

    formRef = null
    formItems = []
    formValues = {}

    timeoutAlertIdentity = null
    editorRef = null
    savedTree = {}

    live = false
    prefixName = null
    formData = null

    initialPlugins = []
    tmpRerender = []
    prevConditionalResult = []
    prevProductsQuantity = {}

    productsStockRef=[]

    nodeExtraDatas=[]

    BASE_URL=BASE_URL

    on(eventName, callback) {
        this.addEventListener(eventName, callback);
    }

    off(eventName, callback) {
        this.removeEventListener(eventName, callback);
    }

    setEditor(ref) {
        this.editorRef = ref
    }

    setTree(t) {
        this.savedTree = t
    }

    setFormRef(ref) {
        this.formRef = ref
    }

    setOrg(org) {
        this.org = org
    }

    getDeviseSymbol(){
        const devise=this.org?.devise || "eur"
        return currencies.currencies.find(i=>i.code===devise.toUpperCase())?.symbol
    }

    currencyFormat(amount) {
        const language=this.org?.language || "fr"
        const devise=this.org?.devise || "eur"

        return new Intl.NumberFormat(language, {
            style: 'currency',
            currency: devise.toUpperCase()
        }).format(amount)
    }

    getCountry(){
        return this.org?.country || "FR"
    }

    setFormValues(values) {
        //console.log("formValues",values)
        this.formValues = values
        this.dispatchEvent(new CustomEvent("formValuesChanges", {detail:values}));
    }

    addProductStockRef(id,callback){
        this.productsStockRef=[...this.productsStockRef.filter(i=>i.id!==id),{id:id,callback:callback}]
    }

    constructor(editorRef = null, formData = null, tree = null, prefixName = null, live = false) {
        super();
        this.editorRef = editorRef
        this.formData = formData
        this.savedTree = tree
        this.prefixName = prefixName
        this.live = live
    }

    isSubmitErrored(data) {
        if (this.formData?.type === "COMPLEX") {

            let findQuantity = false
            for (var p of this.formData.products) {
                if (data[p.id + "_quantity"] > 0) {
                    findQuantity = true
                    break
                }
            }

            if (this.formData.products.length === 0 || this.formData.products.every(p => p.active === false)) {
                findQuantity = true
            }

            if (!findQuantity && !this.formData?.allowNoProduct) {
                notification.error({
                    message: t`Vous devez choisir minimum 1 article.`,
                    placement: "bottomRight"
                })
                return true
            }

            const totalAmount = calculPrice(data, this.formData) / 100
            console.log("TOTAL", totalAmount)
            if (!findQuantity && totalAmount < 1 && !this.formData?.allowZeroAmount) {
                notification.error({
                    message: t`Vous devez choisir minimum 1 article.`,
                    placement: "bottomRight"
                })
                return true
            }
        }

        return false
    }

    registerNamespace(key, formPrefix) {
        const index = this.namespaces.findIndex(i => i.key === key)
        if (index !== -1) {
            this.namespaces[index].formPrefix = formPrefix
        } else {
            this.namespaces.push({key: key, formPrefix: formPrefix})
        }
    }

    getFormPlugins(namespace) {
        return injectLayoutParams([
            formBlock(this, namespace),
            plainText(this, namespace),
            textarea(this, namespace),
            select(this, namespace),
            checkbox(this, namespace),
            date(this, namespace),
            upload(this, namespace),
            contact(this, namespace),
            child(this, namespace),
            hazkara(this, namespace),
            submit(this, namespace),

            identity(this, namespace),
            freeAmount(this, namespace),
            paiementMethod(this, namespace),
            product(this, namespace),
            productInfo(this, namespace),
            productTotal(this, namespace),
            steps(this, namespace),
            condition(this, namespace),
            (Array.isArray(this.formData?.calendars) && this.formData.calendars.length > 0 && calendar(this, namespace)),

            empty(this, namespace),
        ].filter(i => i))
    }

    getContactFormPlugins(namespace) {
        return injectLayoutParams([
            contactDdn(this, namespace),
        ])
    }

    onAddFormPlugin = (ref, id, key) => {
        if (!this.formItems.some(i => i.id == id)) {
            if (!this.live) {
                /*
                if (key.includes("form-identity")) {
                    var identityForms = [ref]
                    var allowed = true

                    for (let p of this.searchPluginKeys("form-identity")) {
                        if (identityForms) {
                            allowed = false
                        }
                        identityForms.push(p)
                    }

                    if (!allowed && !this.timeoutAlertIdentity) {
                        this.timeoutAlertIdentity = setTimeout(() => {
                            this.timeoutAlertIdentity = null
                            notification.warn({
                                message: "Le Block " + ref.pluginConfig.title + " n'est utilisable qu'une seule fois par formulaire."
                            })
                        }, 250)
                    }

                    if (!allowed) {
                        ref.remove()
                        return false
                    }
                }

                 */
            }

            this.formItems.push({ref: ref, id: id, key: key})
            return true

        } else {
            this.formItems = this.formItems.map(i => {
                if (i.id === id) {
                    i.data = ref.data
                }
                return i
            })
            return false
        }
    }

    registerExtraData(nodeId,extraData={},key=null){
        const _index=this.nodeExtraDatas.findIndex(i=>i.nodeId===nodeId && i.key===key)
        if(_index!==-1){
            this.nodeExtraDatas[_index].data=extraData
        }else{
            this.nodeExtraDatas.push({nodeId:nodeId,key:key,data:extraData})
        }
    }

    getExtraData(nodeId,key=null){
        return this.nodeExtraDatas.find(i=>i.nodeId===nodeId && i.key===key)?.data
    }


    async validateForm(callback) {
        if (!this.formRef) {
            return false
        }
        try {
            await this.formRef.validateFields()
            callback()
        } catch (e) {
            return false
        }
    }

    getFormFieldValue(field) {
        if (!this.formRef) {
            return false
        }
        return this.formRef.getFieldValue(field)
    }


    getAlivePlugins() {
        var alives = []
        for (var child of parsePlugins(this.savedTree)) {
            if (this.formItems.some(i => i.id == child.id)) {
                alives.push(child.id)
            }
        }
        return alives
    }


    searchPluginKey(key) {
        for (var child of parsePlugins(this.savedTree)) {
            let plugin = this.formItems.find(i => i.id === child.id)
            if (plugin && plugin.key.includes(key)) {
                return plugin
            }
        }
        return null
    }

    searchPluginKeys(key) {
        let plugins = []
        for (var child of parsePlugins(this.savedTree)) {
            let plugin = this.formItems.find(i => i.id === child.id)
            if (plugin && plugin.key.includes(key)) {
                plugins.push(plugin)
            }
        }
        return plugins
    }

    getPluginData(id) {
        for (var child of parsePlugins(this.savedTree)) {
            if (child.plugin) {

                let prop = "default"
                if (!child?.dataI18n?.default) {
                    prop = Object.keys(child.dataI18n)[0]
                }

                if (child?.dataI18n[prop]?.id === id) {
                    //console.log(child.dataI18n)
                    return child.dataI18n[prop]
                }
            }
        }
        return null
    }


    getPluginsMem(namespace) {
        return [...this.initialPlugins, ...this.getFormPlugins(namespace)]
    }

    getPlugins(initialPlugins = [], namespace = "/") {
        this.initialPlugins = initialPlugins
        return [...this.initialPlugins, ...this.getFormPlugins(namespace)]
    }


    checkFormPlugins() {
        var alives = this.getAlivePlugins()
        var deadPlugins = this.formItems.filter(i => !alives.includes(i.id))
        deadPlugins.forEach(i => this.onRemoveFormPlugin(i.id))
        //console.log(this.formItems)
    }


    onRemoveFormPlugin(id) {
        console.log("onRemoveFormPlugin", id)
        this.formItems = this.formItems.filter((i) => i.id !== id)
    }


    getFormFields(maxNamespace = null) {
        var fields = []
        for (var item of this.formItems) {

            var plugin = parsePlugins(this.savedTree).find(i => i.id === item.id)

            /*
            const pluginNamespace="/"+plugin.plugin.id.split("/")[1]
            //console.log("pluginNamespace",pluginNamespace)

            if(maxNamespace && !maxNamespace.includes(pluginNamespace)){
                continue
            }

             */

            let data = null

            let prop = "default"
            if (!plugin?.dataI18n?.default) {
                prop = Object.keys(plugin.dataI18n)[0]
            }

            if (plugin?.dataI18n[prop]) {
                data = plugin?.dataI18n[prop]
            }

            if (data?.fieldKey) {
                fields.push(data.fieldKey)
            }
        }
        return fields
    }


    getFormFieldsV2(namespace = null) {
        if(namespace==="/"){
            namespace=null
        }else{
            namespace=namespace.replaceAll("/","")
        }

        var fields = []
        for (var item of this.formItems) {

            var plugin = parsePlugins(this.savedTree).find(i => i.id === item.id)


            let data = null

            let prop = "default"
            if (!plugin?.dataI18n?.default) {
                prop = Object.keys(plugin.dataI18n)[0]
            }

            if (plugin?.dataI18n[prop]) {
                data = plugin?.dataI18n[prop]
            }

            if (data?.fieldKey) {
                let label=data.fieldKey

                let _keys=item.key.split("/").filter(i=>i)
                if(_keys.length>1){
                    const _namespace=_keys[1]
                    if(_namespace!==namespace){
                        continue
                    }
                    const product=this.formData.products.find(i=>i.id===_namespace)
                    label="<"+product.label+"> : "+label
                }

                fields.push({label:label,value:data.fieldKey})
            }
        }
        return fields
    }

    hookValueEditor(value) {
        if (!value) {
            return value
        }

        let hookValue = value
        hookValue = migrateNamespace(hookValue)
        //console.log(hookValue)

        for (var id of this.tmpRerender) {
            console.log("Rerender", id)
            hookValue = updateDataTree(hookValue, id, "force_render", new Date().getTime())
        }

        if (this.formData?.manual) {
            hookValue = manualDataTree(hookValue)
        }

        this.tmpRerender = []
        return hookValue
    }

    checkConditions() {
        var needRender = false

        //FORCE RERENDER PRODUCT INFO WHEN QUANTITY CHANGE
        if (this.formData.products) {
            var needRenderProducts = false
            for (var p of this.formData.products) {
                const index = p.id + "_quantity"
                var quantity = this.formValues[index]
                if (quantity !== this.prevProductsQuantity[index]) {
                    needRenderProducts = true
                    this.prevProductsQuantity[index] = quantity
                }
            }

            if (needRenderProducts) {
                parsePlugins(this.savedTree).forEach(p => {
                    if (p.plugin.id.includes("form-product-info") || p.plugin.id.includes("form-product-total")) {
                        needRender = true
                        this.tmpRerender.push(p.id)
                    }
                })
            }
        }

        //console.log("checkConditions")
        var conditonPlugins = parsePlugins(this.savedTree).filter(p => p.plugin.id.includes("form-condition"))
        //console.log("conditonPlugins",conditonPlugins)


        for (var cp of conditonPlugins)
        {
            let prop = "default"
            if (!cp.dataI18n.default) {
                prop = Object.keys(cp.dataI18n)[0]
            }

            const language=this.org?.language
            if(language && cp.dataI18n[language]) {
                prop=language
            }

            let data = cp.dataI18n[prop]
            //console.log("this.formValues",this.formValues,cp)
            const extraDatas=this.nodeExtraDatas.filter(i=>i.nodeId===cp.id)
            let combinedResult=false

            //console.log("extraDatas",extraDatas,extraDatas[0])
            /*
            if(extraDatas && extraDatas[0] && extraDatas[0].key!==null){
                combinedResult=true
            }
             */

            if(combinedResult)
            {
                let results=[]
                for(let ex of extraDatas){
                    const cpIdSplit=cp.plugin.id.split("/").filter(i=>i)
                    const cnamespace=cpIdSplit[cpIdSplit.length-1]
                    console.log(cnamespace)

                    results.push(calculCondition(this.formValues, data.default, data.conditions, data.action,cnamespace,ex.data))
                }
                //console.log(data,result)
                if (this.prevConditionalResult.find(i => i.id === cp.id)) {
                    this.prevConditionalResult = this.prevConditionalResult.map(i => {
                        if (i.id === cp.id) {
                            if (JSON.stringify(i.results) !== JSON.stringify(results)) {
                                needRender = true
                                this.tmpRerender.push(cp.id)
                            }
                            i.results = results
                        }
                        return i
                    })
                } else {
                    needRender = true
                    this.prevConditionalResult.push({id: cp.id, results: results})
                }

            }else
            {
                let result = calculCondition(this.formValues, data.default, data.conditions, data.action)
                //console.log(data,result)

                if (this.prevConditionalResult.find(i => i.id === cp.id)) {
                    this.prevConditionalResult = this.prevConditionalResult.map(i => {
                        if (i.id === cp.id) {
                            if (i.results[0] !== result) {
                                needRender = true
                                this.tmpRerender.push(cp.id)
                            }
                            i.results = [result]
                        }
                        return i
                    })
                } else {
                    needRender = true
                    this.prevConditionalResult.push({id: cp.id, results: [result]})
                }
            }

        }

        //console.log("needRender",needRender)

        if (needRender) {
            this.editorRef.forceUpdate()
        }
    }


    async getAvailableProduct(productId, value = 0,propagation=true) {
        const url = BASE_URL + "/forms/stock/" + productId
        const res = await fetch(url, {
            method: "POST",
            body: JSON.stringify(this.formValues),
            headers: {"Content-type": "Application/json"}
        })
        const data = await res.json()

        if (data.total === null) {
            return Infinity
        }

        if(propagation && data.needPropagation){
            for(let refreshP of this.productsStockRef){
                if(refreshP.id !== productId){
                    //console.log("refreshP.callback()",refreshP.id,productId,refreshP.id !== productId)
                    refreshP.callback()
                }
            }
        }

        return data.total - value
    }

    async getAvailableCalendarSlots(calendarId, month, year) {
        const url = BASE_URL + "/forms/calendar/" + calendarId + "/slots/" + month + "/" + year
        const req = await fetch(url)
        const res = await req.json()
        return res.data.map(i => new Date(i)) || []
    }

    rerenderProducts() {
        parsePlugins(this.savedTree).forEach(p => {
            if (p.plugin.id.includes("form-product-info") || p.plugin.id.includes("form-product-total")) {
                this.tmpRerender.push(p.id)
            }
        })
        this.editorRef.forceUpdate()
    }

    getCalendarsValues() {
        let data = []

        for (let ck of Object.keys(this.formValues).filter(i => i.startsWith("[CALENDAR]_"))) {
            data.push({key: ck, value: this.formValues[ck]})
        }

        return data
    }
}


//UTILS

export function parsePlugins(tree) {
    var plugins = []
    if (!tree) {
        return []
    }

    var children = []
    if (Array.isArray(tree.rows) && tree.rows.length > 0) {
        children = tree.rows
    }

    if (Array.isArray(tree.cells) && tree.cells.length > 0) {
        children = tree.cells
    }

    while (children.length > 0) {
        var next = []
        for (var child of children) {

            if (child.plugin) {
                plugins.push(child)
            }

            if (Array.isArray(child.rows) && child.rows.length > 0) {
                next = next.concat(child.rows)
            }

            if (Array.isArray(child.cells) && child.cells.length > 0) {
                next = next.concat(child.cells)
            }

            children = next
        }
    }

    return plugins
}

export function getPluginField(tree, fieldKey) {
    for (var child of parsePlugins(tree)) {
        if (child.plugin) {

            let prop = "default"
            if (!child.dataI18n.default) {
                prop = Object.keys(child.dataI18n)[0]
            }

            console.log(child.dataI18n)
            if (child?.dataI18n[prop]?.fieldKey === fieldKey) {
                return child
            }
        }
    }
    return null
}

function updateDataTree(tree, id, property, value) {
    if (tree.id === id) {
        if (typeof tree?.dataI18n === "object" && Object.keys(tree.dataI18n).length > 0) {
            //console.log(tree.dataI18n)
            let prop = "default"
            if (!tree.dataI18n.default) {
                prop = Object.keys(tree.dataI18n)[0]
            }
            if (tree.dataI18n[prop]) {
                tree.dataI18n[prop][property] = value;
            }
        }
    }

    if (Array.isArray(tree.cells)) {
        for (var i = 0; i < tree.cells.length; i++) {
            tree.cells[i] = updateDataTree(tree.cells[i], id, property, value);
        }
    }

    if (Array.isArray(tree.rows)) {
        for (var i = 0; i < tree.rows.length; i++) {
            tree.rows[i] = updateDataTree(tree.rows[i], id, property, value);
        }
    }

    return tree;
}


function manualDataTree(tree) {
    //console.log(tree)

    if (typeof tree?.dataI18n === "object" && Object.keys(tree.dataI18n).length > 0) {
        //console.log(tree.dataI18n)
        let props = Object.keys(tree.dataI18n)
        for (let prop of props) {
            if (tree.dataI18n[prop]) {
                tree.dataI18n[prop]["required"] = false;
            }
        }
    }

    if (typeof tree?.plugin === "object" && !tree.plugin.id.startsWith("form")) {
        tree.plugin.id = "form-empty"
        tree.dataI18n = {}
    }

    if (Array.isArray(tree.cells)) {
        for (var i = 0; i < tree.cells.length; i++) {
            tree.cells[i] = manualDataTree(tree.cells[i]);
        }
    }

    if (Array.isArray(tree.rows)) {
        for (var i = 0; i < tree.rows.length; i++) {
            tree.rows[i] = manualDataTree(tree.rows[i]);
        }
    }

    return tree;
}

function migrateNamespace(tree) {
    if (tree.plugin && tree.plugin.id.includes("form") && !tree.plugin.id.includes("/")) {
        tree.plugin.id = tree.plugin.id + "/";
    }

    if (tree.rows) {
        tree.rows = tree.rows.map(i => migrateNamespace(i))
    }

    if (tree.cells) {
        tree.cells = tree.cells.map(i => migrateNamespace(i))
    }

    return tree;
}


function getFlatFormValues(formValues = {}) {
    let _formValues = []

    for (let k of Object.keys(formValues || {})) {
        if (!formValues[k]) {
            continue
        }

        if (typeof formValues[k] !== "object") {
            _formValues.push({key: k, value: formValues[k]})
        } else {
            _formValues = _formValues.concat(getFlatFormValues(formValues[k]))
        }
    }

    return _formValues
}

export function calculCondition(formValues, defaultAction, conditions, action,namespace=null,extraData={}) {
    //console.group(defaultAction,conditions,action)

    if(typeof formValues !== "object"){
     formValues={}
    }

    const flatFormValues = getFlatFormValues(formValues)
    //console.log("flatFormValues",flatFormValues)

    var results = []
    for (var c of conditions) {
        let operator = "&&"

        if (!c) {
            results.push({bool: false, operator: "operator"})
            continue
        }

        if (c.next === "OR") {
            operator = "||"
        }

        //var fieldKey=flatFormValues.find(i=>removeFieldType(i.key) === c.field)?.key
        var fieldKey = Object.keys(formValues).find(i => removeFieldType(i) === c.field)
        let productKey = false
        let flatKey = false

        //console.log(formValues[namespace],extraData)
        /*
        if (!fieldKey && namespace && extraData && formValues[namespace] && formValues[namespace][extraData.name]) {
            //console.log("productKey",formValues[namespace][extraData.name])
            productKey = true
            fieldKey = Object.keys(formValues[namespace][extraData.name] || {}).find(i => removeFieldType(i) === c.field)
        }
         */

        //if (!fieldKey && !namespace) {
        if (!fieldKey) {
            flatKey = true
            fieldKey = flatFormValues.find(i => removeFieldType(i.key) === c.field)?.key
        }

        if (!fieldKey) {
            results.push({bool: false, operator: operator})
            continue
        }


        var fieldValue = formValues[fieldKey]

        /*
        if (productKey) {
            //fieldValue = flatFormValues.find(i => i.key === fieldKey)?.value
            fieldValue = formValues[namespace][extraData.name][fieldKey]
        }
         */

        if (flatKey) {
            fieldValue = flatFormValues.find(i => i.key === fieldKey)?.value
        }

        //console.log(extraData?.name,fieldKey,fieldValue)

        if (typeof fieldValue == "string") {
            fieldValue = fieldValue.trim()
        }

        var finalValue = fieldValue
        if (!Array.isArray(fieldValue)) {
            finalValue = [fieldValue]
        }

        var matchValues = c.values || []

        //console.log(finalValue,matchValues,c)

        var bool = false
        switch (c.type) {
            case CONDITIONS[0]: //EQUAL
                bool = finalValue.some(i => matchValues.includes(i))
                break
            case CONDITIONS[1]: //GTE
                bool = finalValue.some(i => matchValues.some(j => i >= j))
                break
            case CONDITIONS[2]: //LTE
                bool = finalValue.some(i => matchValues.some(j => i <= j))
                break
            case CONDITIONS[3]: //NOT EQUAL
                bool = finalValue.some(i => matchValues.every(j => i != j))
                break
            case CONDITIONS[4]: //EMPTY
                bool = (fieldValue === "" || fieldValue === null || fieldValue === undefined)
                break
            case CONDITIONS[5]: //NOT EMPTY
                bool = (fieldValue !== "" && fieldValue !== null && fieldValue !== undefined)
                break
            case CONDITIONS[6]: //CHECKED
                bool = (fieldValue === true)
                break
        }
        //console.log(bool)

        results.push({bool: bool, operator: operator})
    }

    var evalString = ""
    for (var [index, r] of results.entries()) {
        evalString += " " + r.bool + " "
        if (index < results.length - 1) {
            evalString += " " + r.operator + " "
        }
    }

    var match = eval(evalString)
    //console.log(evalString,match)

    //console.groupEnd()

    return match ? action : defaultAction
}


export function calculPrice(formValues, formData) {
    let total = 0
    let products = formData.products

    if (formData.withPaiement) {
        for (var p of products) {
            const quantity = formValues[p.id + "_quantity"] || 0
            total += Math.round(p.montant * 100) * quantity
        }
    }


    //FREE AMOUNT
    for (var k of Object.keys(formValues)) {
        if (k.startsWith("[FREE_AMOUNT]_")) {
            var value = formValues[k]
            if (value > 0) {
                total += Math.round(value * 100)
            }
        }
    }

    return total
}


//EXTRA

/*
    @deprecated
*/
export function applyEditorStyle() {
    return false

    try {
        const drawer = document.getElementsByClassName("css-12i7wg6-MuiPaper-root-MuiDrawer-paper")[0]

        new MutationObserver(function (mutations) {
            for (var item of document.getElementsByClassName("react-page-toolbar-draggable")) {
                const title = item.getElementsByClassName("MuiListItemText-primary")[0]
                const description = item.getElementsByClassName("MuiListItemText-secondary")[0]
                var icon = item.getElementsByClassName("MuiAvatar-root")[0]

                //console.log(title,description,icon)
                if (description.textContent === "Element de formulaire utilisable.") {
                    icon.style.backgroundColor = "#F39C12"
                }

                if (title.textContent === "Block Identitaire") {
                    icon.style.backgroundColor = "#138D75"
                }

                if (title.textContent === "Étapes") {
                    icon.style.backgroundColor = "#BA4A00"
                }

                if (title.textContent === "Étape") {
                    icon.style.backgroundColor = "#BA4A00"
                }

                if (title.textContent === "Condition") {
                    icon.style.backgroundColor = "#2980B9"
                }
            }
        }).observe(drawer, {attributes: true, childList: true, subtree: true, characterData: true})

    } catch (e) {
        console.error(e)
    }
}
